import { type JSX, useCallback, useMemo, useState } from 'react'

import { FilterCollection } from '@hap/analytics'
import {
  Command,
  CommandCheckItem,
  CommandExpandContext,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Icon,
  Spinner,
} from '@hap/components'

import { useDatastore } from '../datastore'
import { CommandSelectAll } from './CommandSelectAll'
import { BaseCommandListProps } from './types'

export function WorkspacesCommandList<T extends boolean = false>({
  selected = [],
  ...props
}: BaseCommandListProps<T, string>): JSX.Element {
  const [condensedList, setCondensedList] = useState(true)
  const [query, setQuery] = useState('')
  const { data, isInitialLoading } = useDatastore(FilterCollection.Workspaces)

  const filteredList = useMemo(() => {
    return condensedList
      ? data?.filter((workspace) => {
          if (props.excludeIds) return !props.excludeIds.includes(workspace.id)
          return true
        })
      : data
  }, [data, condensedList, props.excludeIds])

  const isSearching = query.trim().length > 0

  const visibleItems = useMemo(() => {
    if (!isSearching) return filteredList
    const normalizedQuery = query.toLowerCase()
    return filteredList?.filter((workspace) =>
      workspace.name.toLowerCase().includes(normalizedQuery),
    )
  }, [filteredList, query, isSearching])

  const handleSelectAll = useCallback(
    (newSelection: string[]) => {
      props.onSelect(newSelection)
    },
    [props.onSelect],
  )

  const handleToggleItem = useCallback(
    (id: string) => {
      if (props.multiselect) {
        // Multi-select: toggle the item
        const newSelection = selected.includes(id)
          ? selected.filter((s) => s !== id)
          : [...selected, id]
        props.onSelect(newSelection)
      } else {
        // Single-select: replace with just this item
        props.onSelect([id])
      }
    },
    [props.multiselect, selected, props.onSelect],
  )

  const handleClear = useCallback(() => {
    props.onSelect([])
  }, [props.onSelect])

  const expandedResults = (data?.length || 0) - (filteredList?.length || 0)

  return isInitialLoading ? (
    <div className="flex aspect-square items-center justify-center">
      <Spinner size="40" />
    </div>
  ) : (
    <Command>
      <CommandInput
        placeholder="Filter..."
        autoFocus
        value={query}
        onValueChange={setQuery}
      />
      <CommandList className="py-1">
        <CommandGroup>
          <CommandSelectAll
            visibleItems={visibleItems || []}
            selectedIds={selected}
            onSelect={handleSelectAll}
            query={query}
            multiselect={props.multiselect}
          />
          {props.allowEmpty && (
            <CommandItem onSelect={handleClear} className="gap-2">
              <Icon name="x" />
              {props.allowEmptyLabel || 'Clear'}
            </CommandItem>
          )}
          {visibleItems?.map((workspace) => (
            <CommandCheckItem
              key={workspace.id}
              value={`${workspace.name}`}
              className="group mx-1 flex gap-3"
              onSelect={() => handleToggleItem(workspace.id)}
              selected={selected.includes(workspace.id)}
              multiselect={props.multiselect}
            >
              {workspace.name}
            </CommandCheckItem>
          ))}
          <CommandExpandContext
            availableResults={expandedResults}
            onSelect={() => setCondensedList(false)}
          />
        </CommandGroup>
      </CommandList>
    </Command>
  )
}
